import { Link } from "react-router-dom";
import "./mutual-fund-sections.css";

const GOALS = [
  {
    title: "Retirement Planning",
    desc: "Build a secure corpus for your post-retirement life.",
    strategy: "Equity + Debt Mix",
    horizon: "10+ Years",
    icon: "fa-umbrella-beach",
    tone: "blue",
  },
  {
    title: "Child Education",
    desc: "Plan for your child's higher education with confidence.",
    strategy: "Equity Oriented",
    horizon: "5–15 Years",
    icon: "fa-graduation-cap",
    tone: "green",
  },
  {
    title: "Wealth Creation",
    desc: "Grow your wealth and achieve financial freedom.",
    strategy: "Equity Growth",
    horizon: "7+ Years",
    icon: "fa-chart-column",
    tone: "gold",
  },
  {
    title: "Tax Saving",
    desc: "Save taxes and grow your money simultaneously.",
    strategy: "ELSS Funds",
    horizon: "3+ Years",
    icon: "fa-file-invoice-dollar",
    tone: "purple",
  },
  {
    title: "Emergency Fund",
    desc: "Stay prepared for unexpected expenses.",
    strategy: "Liquid Funds",
    horizon: "Short Term",
    icon: "fa-shield-halved",
    tone: "orange",
  },
];

const STATS = [
  { value: "₹ 2.4L Cr+", label: "AUM Managed" },
  { value: "15+", label: "Years of Experience" },
  { value: "10,000+", label: "Happy Investors" },
  { value: "250+", label: "Top Mutual Fund Schemes" },
];

const ALLOCATION = [
  { label: "Equity Funds", pct: 68, color: "#1e4d8c" },
  { label: "Debt Funds", pct: 18, color: "#3b82f6" },
  { label: "Hybrid Funds", pct: 10, color: "#f59e0b" },
  { label: "Others", pct: 4, color: "#94a3b8" },
];

const RETURNS = [
  { period: "1Y", value: "18.62%" },
  { period: "3Y", value: "16.35%" },
  { period: "5Y", value: "15.21%" },
  { period: "10Y", value: "13.48%" },
];

function GrowthChart() {
  return (
    <svg
      className="mf-growth-chart"
      viewBox="0 0 320 160"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="mfGrowthFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#139bb2" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#139bb2" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[40, 80, 120].map((y) => (
        <line
          key={y}
          x1="36"
          y1={y}
          x2="310"
          y2={y}
          stroke="#e8eef3"
          strokeWidth="1"
        />
      ))}
      <text x="4" y="44" className="mf-chart-axis">20L</text>
      <text x="4" y="84" className="mf-chart-axis">15L</text>
      <text x="4" y="124" className="mf-chart-axis">10L</text>
      <text x="8" y="154" className="mf-chart-axis">5L</text>
      <path
        d="M40 130 C70 125, 90 118, 110 105 C140 85, 160 95, 185 70 C210 48, 240 55, 270 32 C290 20, 300 18, 310 14 L310 155 L40 155 Z"
        fill="url(#mfGrowthFill)"
      />
      <path
        d="M40 130 C70 125, 90 118, 110 105 C140 85, 160 95, 185 70 C210 48, 240 55, 270 32 C290 20, 300 18, 310 14"
        fill="none"
        stroke="#139bb2"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <circle cx="310" cy="14" r="4" fill="#139bb2" />
      <text x="40" y="158" className="mf-chart-axis">May '23</text>
      <text x="265" y="158" className="mf-chart-axis">May '24</text>
    </svg>
  );
}

function AllocDonut() {
  const radius = 38;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="mf-alloc-wrap">
      <svg width="110" height="110" viewBox="0 0 110 110" aria-hidden="true">
        {ALLOCATION.map((a) => {
          const length = (a.pct / 100) * circumference;
          const el = (
            <circle
              key={a.label}
              cx="55"
              cy="55"
              r={radius}
              fill="none"
              stroke={a.color}
              strokeWidth="14"
              strokeDasharray={`${length} ${circumference - length}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 55 55)"
            />
          );
          offset += length;
          return el;
        })}
        <circle cx="55" cy="55" r="26" fill="#fff" />
      </svg>
      <ul className="mf-alloc-legend">
        {ALLOCATION.map((a) => (
          <li key={a.label}>
            <span className="dot" style={{ background: a.color }} />
            <span className="name">{a.label}</span>
            <span className="pct">{a.pct}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MutualFundGoalsInsights() {
  return (
    <>
      <section className="mf-goals" id="why-choose">
        <div className="mf-goals-inner">
          <header className="mf-sec-header">
            <p className="mf-sec-eyebrow">INVEST FOR WHAT MATTERS</p>
            <h2 className="mf-sec-title">Goal-Based Investing for Your Life</h2>
          </header>

          <div className="mf-goals-grid">
            {GOALS.map((g) => (
              <article key={g.title} className={`mf-goal-card tone-${g.tone}`}>
                <div className="mf-goal-icon" aria-hidden="true">
                  <i className={`fa-solid ${g.icon}`} />
                </div>
                <h3>{g.title}</h3>
                <p>{g.desc}</p>
                <div className="mf-goal-meta">
                  <div>
                    <span>Strategy</span>
                    <strong>{g.strategy}</strong>
                  </div>
                  <div>
                    <span>Horizon</span>
                    <strong>{g.horizon}</strong>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mf-insights" id="insights">
        <div className="mf-insights-inner">
          <header className="mf-sec-header">
            <p className="mf-sec-eyebrow">PERFORMANCE & INSIGHTS</p>
            <h2 className="mf-sec-title">
              Data-Driven Insights for Smarter Decisions
            </h2>
          </header>

          <div className="mf-insights-panel">
            <div className="mf-insights-col mf-insights-growth">
              <h3>Portfolio Growth</h3>
              <p className="mf-insights-sub">Value of ₹10,00,000 Invested</p>
              <div className="mf-insights-value-row">
                <strong>₹18,62,450</strong>
                <span className="gain">▲ 18.62% (1Y)</span>
              </div>
              <GrowthChart />
            </div>

            <div className="mf-insights-col mf-insights-mid">
              <div className="mf-market-box">
                <h3>Market Insight</h3>
                <p>
                  Indian equity markets remain positive supported by strong
                  economic growth and corporate earnings.
                </p>
                <Link to="/blog" className="mf-market-link">
                  View Market Updates →
                </Link>
              </div>

              <div className="mf-key-stats">
                <h3>Key Statistics</h3>
                <div className="mf-key-stats-grid">
                  {STATS.map((s) => (
                    <div key={s.label} className="mf-key-stat">
                      <strong>{s.value}</strong>
                      <span>{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mf-insights-col mf-insights-right">
              <div>
                <h3>Asset Allocation</h3>
                <AllocDonut />
              </div>
              <div className="mf-cagr">
                <h3>Historical Returns (CAGR)</h3>
                <div className="mf-cagr-row">
                  {RETURNS.map((r) => (
                    <div key={r.period}>
                      <span>{r.period}</span>
                      <strong>{r.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
